import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import { S_LocationDot } from 'hosman-material';
import TextInput from './TextInput';
import { initWoosmapScript, extractAddress } from '../../../../../../../../shared/utils/mapService';

const AddressInput = ({ name = 'address', value, label, onChange, errors = {}, placeholder, listPlacement = 'top-10' }) => {
  const [stateValue, setStateValue] = useState(value || '');
  const [predictions, setPredictions] = useState([]);
  const [displayPredictions, setDisplayPredictions] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const inputRef = useRef(null);
  const debounceTimer = useRef(null);
  const localitiesServiceRef = useRef(null);

  // Init Woosmap une seule fois
  useEffect(() => {
    initWoosmapScript().then(() => {
      localitiesServiceRef.current = new woosmap.map.LocalitiesService();
    });
    return () => clearTimeout(debounceTimer.current);
  }, []);

  // Ferme les suggestions au clic extérieur
  useEffect(() => {
    const handler = () => setDisplayPredictions(false);
    window.addEventListener('click', handler);
    return () => window.removeEventListener('click', handler);
  }, []);

  // Focus au montage si pas de valeur
  useEffect(() => {
    if (inputRef.current && !stateValue) inputRef.current.focus();
  }, []);

  // Autocomplete au changement de valeur
  useEffect(() => {
    initAutocomplete();
  }, [stateValue]);

  // Sync value prop externe
  useEffect(() => {
    setStateValue(value || '');
  }, [value]);

  function initAutocomplete() {
    if (stateValue.length < 2 || !localitiesServiceRef.current) {
      setPredictions([]);
      return;
    }

    clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      localitiesServiceRef.current
        .autocomplete({
          input: stateValue,
          types: ['locality', 'address'],
          language: 'FR',
          components: { country: ['fr-fr'] },
        })
        .then((response) => {
          setPredictions(response.localities);
          if (document.activeElement === inputRef.current) setDisplayPredictions(true);
        })
        .catch((error) => console.error('Woosmap error:', error));
    }, 250);
  }

  const selectOption = async (prediction) => {
    setDisplayPredictions(false);
    setSelectedIndex(-1);
    try {
      const result = await localitiesServiceRef.current.getDetails({ publicId: prediction.public_id });
      const addressObj = extractAddress(name, result.result);
      if (!addressObj) return;
      setStateValue(addressObj[name] || prediction.description);
      onChange(addressObj);
    } catch (error) {
      console.error('Woosmap getDetails error:', error);
    }
  };

  const handleChange = (e) => {
    setStateValue(e.target.value);
    onChange({ [name]: e.target.value });
  };

  const handleKeyDown = (event) => {
    if (!displayPredictions || predictions.length === 0) return;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        setSelectedIndex(prev => Math.min(prev + 1, predictions.length - 1));
        break;
      case 'ArrowUp':
        event.preventDefault();
        setSelectedIndex(prev => Math.max(prev - 1, 0));
        break;
      case 'Enter':
        event.preventDefault();
        if (selectedIndex >= 0) selectOption(predictions[selectedIndex]);
        break;
    }
  };

  return (
    <div className='relative' data-testid='AddressInput'>
      <TextInput
        refProp={inputRef}
        name={name}
        label={label}
        value={stateValue}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        errors = {errors}
        placeholder={placeholder}
        paddings='pl-8'
        icon={S_LocationDot}
      />

      {displayPredictions && predictions.length > 0 &&
        <ul className={`z-50 absolute w-full bg-white rounded-2xl border border-ih-purple-20 overflow-y-scroll scrollbar-hide mt-2 ${listPlacement}`}>
          {predictions.map((prediction, index) => {
            return (
              <li
                key={prediction.public_id}
                onClick={() => selectOption(prediction)}
                className={`text-ih-indigo first:rounded-t-2xl last:rounded-b-2xl hover:bg-ih-purple-5 cursor-pointer select-none relative py-3 px-3 border-b border-b-ih-purple-10 ${index === selectedIndex ? 'bg-ih-purple-5' : ''}`}
              >
                {prediction.description}
              </li>
            )
          })}
        </ul>
      }
    </div>
  );
};

export default AddressInput;

AddressInput.propTypes = {
  name: PropTypes.string,
  label: PropTypes.string,
  value: PropTypes.string,
  onChange: PropTypes.func,
  errors: PropTypes.object,
  placeholder: PropTypes.string,
  listPlacement: PropTypes.string
};
